import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';

const SOURCE = 'free-gift-cart-sync';

/** @see https://rokt.notion.site/UpCart-Public-API-306ee2db860881feb300f0e09d08048f#306ee2db8608812aaf09faee07136420 */

function isUpCartPresent() {
  if (window.themeGlobals?.upcartEnabled === true) return true;

  try {
    for (const e of performance.getEntriesByType('resource')) {
      if (typeof e.name === 'string' && /upcart/i.test(e.name)) return true;
    }
  } catch (_) {
    /* ignore */
  }

  for (const el of document.querySelectorAll('script[src], link[href]')) {
    const u = el.getAttribute('src') || el.getAttribute('href') || '';
    if (/upcart/i.test(u)) return true;
  }

  return !!document.querySelector(
    '.upcart-cart, .upcart-backdrop, .upcart-public-header, #upCartStickyButton'
  );
}

let didLogUpCart = false;
function logUpCartIntegrationOnce() {
  if (didLogUpCart || !isUpCartPresent()) return;
  didLogUpCart = true;
}

function scheduleUpCartIntegrationLog() {
  logUpCartIntegrationOnce();
  for (const ms of [400, 2000]) setTimeout(logUpCartIntegrationOnce, ms);
}

const UPCART_HIDE_THEME_CART_DRAWER_STYLE_ID = 'free-gift-upcart-hide-theme-cart-drawer';

function suppressThemeCartDrawerWhenUpcartActive() {
  if (!isUpCartPresent()) return;
  // if (!document.getElementById(UPCART_HIDE_THEME_CART_DRAWER_STYLE_ID)) {
  //   const el = document.createElement('style');
  //   el.id = UPCART_HIDE_THEME_CART_DRAWER_STYLE_ID;
  //   el.textContent = ':root dialog.cart-drawer__dialog { display: none !important; }';
  //   document.head.appendChild(el);
  // }
  const drawer = /** @type {HTMLElement & { close?: () => void } | null} */ (
    document.querySelector('cart-drawer-component')
  );
  if (drawer) {
    drawer.removeAttribute('auto-open');
    try {
      drawer.close?.();
    } catch (_) {
      /* ignore */
    }
  }
}

function registerUpCartVersusThemeDrawer() {
  document.addEventListener(
    ThemeEvents.cartUpdate,
    () => {
      if (!isUpCartPresent()) return;
      suppressThemeCartDrawerWhenUpcartActive();
    },
    true
  );
}

function scheduleSuppressThemeDrawerForUpcart() {
  suppressThemeCartDrawerWhenUpcartActive();
  for (const ms of [50, 200, 600, 2000, 5000]) setTimeout(suppressThemeCartDrawerWhenUpcartActive, ms);
}

function getUpcartPublicApi() {
  const w = /** @type {Window & { upcartRefreshCart?: () => unknown; upcartSubscribeCartUpdated?: (fn: () => void) => void }} */ (
    window
  );
  return { refresh: w.upcartRefreshCart, subscribe: w.upcartSubscribeCartUpdated };
}

function isUpCartPublicApiAvailable() {
  return typeof getUpcartPublicApi().refresh === 'function';
}

/** @param {unknown} v */
function runMaybeAsync(v) {
  if (v != null && typeof /** @type {{ then?: unknown }} */ (v).then === 'function') {
    return /** @type {Promise<unknown>} */ (v).catch(() => {});
  }
  return Promise.resolve();
}

function notifyUpCartCartChanged() {
  const { refresh } = getUpcartPublicApi();
  if (!isUpCartPresent() || typeof refresh !== 'function') return;
  try {
    void runMaybeAsync(refresh());
  } catch (e) {
    console.warn('Free gift cart: upcartRefreshCart', e);
  }
}

/** Line item properties on the auto-added gift (visible in cart & checkout). */
const FREE_GIFT_PROPERTIES = { _free_gift: 'free-gift' };

/** @returns {{ giftVariantId: number; triggerProductIds: Set<number> } | null} */
function getConfig() {
  const fg = window.themeGlobals?.freeGiftCart;
  if (!fg?.giftVariantId) return null;
  const raw = fg.triggerProductIds;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const triggerProductIds = new Set(raw.map((id) => Number(id)));
  return { giftVariantId: Number(fg.giftVariantId), triggerProductIds };
}

function collectSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return [...new Set(ids)];
}

async function fetchCartJson() {
  const res = await fetch(`${Theme.routes.root}cart.js`, { headers: { 'Cache-Control': 'no-store' } });
  return res.json();
}

/**
 * @param {any} cart
 * @param {Set<number>} triggerProductIds
 */
function cartHasTriggerProduct(cart, triggerProductIds) {
  return (cart.items || []).some((line) => triggerProductIds.has(Number(line.product_id)));
}

/** Same variant_id can appear as a paid line — gift lines are marked with _free_gift. */
function lineIsFreeGift(line, giftVariantId) {
  if (Number(line.variant_id) !== Number(giftVariantId)) return false;
  const p = line.properties;
  if (p == null) return false;
  if (typeof p === 'object' && !Array.isArray(p)) {
    return String(p._free_gift) === 'free-gift';
  }
  if (Array.isArray(p)) {
    return p.some((x) => x && x.name === '_free_gift' && String(x.value) === 'free-gift');
  }
  return false;
}

/**
 * @param {any} cart
 * @param {number} giftVariantId
 */
function findFreeGiftLines(cart, giftVariantId) {
  return (cart.items || []).filter((line) => lineIsFreeGift(line, giftVariantId));
}

/**
 * Shopify /cart/change.js expects `line` as a 1-based integer index (not the string `key`).
 * @param {any} cart
 * @param {any} lineItem
 * @returns {number | null}
 */
function lineIndexOneBased(cart, lineItem) {
  const items = cart.items || [];
  const idx = items.indexOf(lineItem);
  return idx === -1 ? null : idx + 1;
}

/**
 * @param {number} giftVariantId
 * @param {string} sectionsParam
 */
async function postAddGift(giftVariantId, sectionsParam) {
  const body = {
    items: [{ id: giftVariantId, quantity: 1, properties: { ...FREE_GIFT_PROPERTIES } }],
  };
  if (sectionsParam) body.sections = sectionsParam;
  const res = await fetch(
    `${Theme.routes.root}cart/add.js`,
    fetchConfig('json', { body: JSON.stringify(body) })
  );
  return res.json();
}

/**
 * @param {number} lineOneBased
 * @param {number} quantity
 * @param {string} sectionsParam
 */
async function postChangeLine(lineOneBased, quantity, sectionsParam) {
  const requestBody = { line: lineOneBased, quantity };
  if (sectionsParam) requestBody.sections = sectionsParam;
  const res = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: JSON.stringify(requestBody) }));
  return res.json();
}

/**
 * @param {any} parsed
 */
function dispatchCartRefresh(parsed) {
  document.dispatchEvent(
    new CartUpdateEvent(parsed, 'free-gift-cart', {
      itemCount: parsed.item_count,
      source: SOURCE,
      sections: parsed.sections,
    })
  );
  notifyUpCartCartChanged();
}

/**
 * Collapse multiple gift lines and enforce quantity === 1 on the remaining line.
 * @param {any} cart
 * @param {number} giftVariantId
 * @param {string} sectionsParam
 */
async function normalizeFreeGiftQuantity(cart, giftVariantId, sectionsParam) {
  let current = cart;
  let changed = false;

  let lines = findFreeGiftLines(current, giftVariantId);
  while (lines.length > 1) {
    const lineOneBased = lineIndexOneBased(current, lines[lines.length - 1]);
    if (lineOneBased == null) break;
    const data = await postChangeLine(lineOneBased, 0, sectionsParam);
    if (data.errors) {
      console.warn('Free gift cart:', data.errors);
      return;
    }
    current = await fetchCartJson();
    changed = true;
    lines = findFreeGiftLines(current, giftVariantId);
  }

  lines = findFreeGiftLines(current, giftVariantId);
  if (lines.length === 1 && Number(lines[0].quantity) !== 1) {
    const lineOneBased = lineIndexOneBased(current, lines[0]);
    if (lineOneBased == null) return;
    const data = await postChangeLine(lineOneBased, 1, sectionsParam);
    if (data.errors) {
      console.warn('Free gift cart:', data.errors);
      return;
    }
    current = data.items ? data : await fetchCartJson();
    changed = true;
  }

  if (changed) dispatchCartRefresh(current);
}

/**
 * @param {any} cart
 * @param {number} giftVariantId
 * @param {string} sectionsParam
 */
async function removeAllFreeGiftLines(cart, giftVariantId, sectionsParam) {
  let current = cart;
  let lines = findFreeGiftLines(current, giftVariantId);
  let changed = false;

  while (lines.length) {
    const lineOneBased = lineIndexOneBased(current, lines[0]);
    if (lineOneBased == null) break;
    const data = await postChangeLine(lineOneBased, 0, sectionsParam);
    if (data.errors) {
      console.warn('Free gift cart:', data.errors);
      return;
    }
    current = await fetchCartJson();
    changed = true;
    lines = findFreeGiftLines(current, giftVariantId);
  }

  if (changed) dispatchCartRefresh(current);
}

let syncChain = Promise.resolve();

function enqueueSync() {
  syncChain = syncChain.then(() => syncFreeGiftCart()).catch((e) => console.error(e));
}

async function syncFreeGiftCart() {
  const cfg = getConfig();
  if (!cfg) return;

  const sectionsParam = collectSectionIds().join(',');
  const { giftVariantId, triggerProductIds } = cfg;
  let cart;
  try {
    cart = await fetchCartJson();
  } catch (e) {
    console.error(e);
    return;
  }

  const wantsGift = cartHasTriggerProduct(cart, triggerProductIds);
  const giftLines = findFreeGiftLines(cart, giftVariantId);
  const totalGiftQty = giftLines.reduce((s, line) => s + Number(line.quantity || 0), 0);

  try {
    if (wantsGift) {
      if (giftLines.length === 0) {
        const data = await postAddGift(giftVariantId, sectionsParam);
        if (data.status && data.message) {
          console.warn('Free gift cart:', data.message);
          return;
        }
        dispatchCartRefresh(data);
        return;
      }
      if (giftLines.length > 1 || totalGiftQty !== 1) {
        await normalizeFreeGiftQuantity(cart, giftVariantId, sectionsParam);
      }
      return;
    }

    if (giftLines.length) {
      await removeAllFreeGiftLines(cart, giftVariantId, sectionsParam);
    }
  } catch (e) {
    console.error(e);
  }
}

const debouncedSync = debounce(() => enqueueSync(), 120);

let upcartCartUpdatedSubscribed = false;
function subscribeUpCartCartUpdated() {
  const { subscribe } = getUpcartPublicApi();
  if (upcartCartUpdatedSubscribed || typeof subscribe !== 'function') return;
  upcartCartUpdatedSubscribed = true;
  subscribe(() => debouncedSync());
}

function scheduleUpCartPublicApiHooks() {
  subscribeUpCartCartUpdated();
  for (const ms of [400, 2000, 5000]) setTimeout(subscribeUpCartCartUpdated, ms);
}

function onCartUpdate(event) {
  if (event?.detail?.data?.source === SOURCE) return;
  debouncedSync();
}

if (getConfig()) {
  scheduleUpCartIntegrationLog();
  scheduleUpCartPublicApiHooks();
  registerUpCartVersusThemeDrawer();
  scheduleSuppressThemeDrawerForUpcart();
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enqueueSync(), { once: true });
  } else {
    enqueueSync();
  }
}
